import {
  VENUE_PHOTO_ASPECT,
  VENUE_PHOTO_MIN_HEIGHT_PX,
  VENUE_PHOTO_MIN_WIDTH_PX,
} from '@bonsai/shared';

import type { PhotoCropArea } from '../../../../../api/venue-photo';

export interface PhotoSize {
  width: number;
  height: number;
}

/** Where the crop box sits over the photo: 0 is the left/top edge, 1 the right/bottom edge. */
export interface CropPan {
  x: number;
  y: number;
}

export const MAX_ZOOM = 3;
/** Below this much zoom headroom the slider is switched off and the whole photo is used. */
export const MIN_ZOOM_HEADROOM = 1.25;
/** A photo this close to square, or taller, loses most of itself in a widescreen crop. */
const NEAR_SQUARE_RATIO = 1.15;

const aspectOf = (photo: PhotoSize) => photo.width / photo.height;

/** Source pixels across the 16:9 box when the photo just covers it (zoom 1). */
function coverWidth(photo: PhotoSize): number {
  return aspectOf(photo) >= VENUE_PHOTO_ASPECT ? photo.height * VENUE_PHOTO_ASPECT : photo.width;
}

/** The most zoom allowed: stops where the crop would fall under the booking site's minimum size. */
export function maxZoomFor(photo: PhotoSize): number {
  return Math.min(MAX_ZOOM, coverWidth(photo) / VENUE_PHOTO_MIN_WIDTH_PX);
}

export function canZoom(photo: PhotoSize): boolean {
  return maxZoomFor(photo) >= MIN_ZOOM_HEADROOM;
}

export function isPortraitOrNearSquare(photo: PhotoSize): boolean {
  return aspectOf(photo) < NEAR_SQUARE_RATIO;
}

/** Keeps a requested zoom inside what this photo allows (no zoom at all when it is switched off). */
export function clampZoom(photo: PhotoSize, zoom: number): number {
  if (!canZoom(photo)) return 1;
  return Math.min(maxZoomFor(photo), Math.max(1, zoom));
}

const clampUnit = (v: number) => Math.min(1, Math.max(0, v));

/**
 * The crop area to send, in whole pixels of the photo as it looks on screen. The browser shows
 * the photo upright using its rotation data, and the server crops after applying the same
 * rotation, so both count pixels the same way.
 */
export function cropAreaFor(photo: PhotoSize, zoomRequested: number, pan: CropPan): PhotoCropArea {
  const zoom = clampZoom(photo, zoomRequested);
  let width = Math.min(photo.width, Math.round(coverWidth(photo) / zoom));
  let height = Math.round(width / VENUE_PHOTO_ASPECT);
  if (height > photo.height) {
    width = Math.floor(photo.height * VENUE_PHOTO_ASPECT);
    height = Math.round(width / VENUE_PHOTO_ASPECT);
  }
  const x = Math.round(clampUnit(pan.x) * (photo.width - width));
  const y = Math.round(clampUnit(pan.y) * (photo.height - height));
  return { x, y, width, height };
}

/** True when even the widest 16:9 crop of this photo is under the booking site's minimum size. */
export function isBelowMinimumSize(photo: PhotoSize): boolean {
  const crop = cropAreaFor(photo, 1, { x: 0.5, y: 0.5 });
  return crop.width < VENUE_PHOTO_MIN_WIDTH_PX || crop.height < VENUE_PHOTO_MIN_HEIGHT_PX;
}

/** Where the crop box starts: centred, a little high on tall photos where faces and signs sit. */
export function startingPan(photo: PhotoSize): CropPan {
  return { x: 0.5, y: isPortraitOrNearSquare(photo) ? 0.42 : 0.5 };
}

/** Size and offset of the photo inside the crop box, as percentages of the box. */
export interface PhotoPlacement {
  widthPct: number;
  heightPct: number;
  leftPct: number;
  topPct: number;
}

export function placementFor(
  photo: PhotoSize,
  zoomRequested: number,
  pan: CropPan,
): PhotoPlacement {
  const zoom = clampZoom(photo, zoomRequested);
  const ratio = aspectOf(photo);
  const wide = ratio >= VENUE_PHOTO_ASPECT;
  const widthPct = (wide ? (ratio / VENUE_PHOTO_ASPECT) * 100 : 100) * zoom;
  const heightPct = (wide ? 100 : (VENUE_PHOTO_ASPECT / ratio) * 100) * zoom;
  return {
    widthPct,
    heightPct,
    leftPct: -(widthPct - 100) * clampUnit(pan.x),
    topPct: -(heightPct - 100) * clampUnit(pan.y),
  };
}
